import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate, type LoaderFunctionArgs } from 'react-router-dom';
import { cn, Table, TableBody, TableCell, TableHeader, TableRow } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import {
  VALUATION_ADMIN_STATUS_FILTERS,
  VALUATION_ADMIN_STATUSES,
  type AdminOrganizationListItem,
  type AdminValuationQueueItem,
  type ListAdminValuationRequestsQuery,
  type ValuationAdminStatus,
  type ValuationAdminStatusFilter,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { claimHolderLabel } from '@/lib/valuations/claim-holder-label';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { FilterBar, FilterPill } from '@/components/filter-bar';
import { SEARCH_PARAM, useListParams } from '@/lib/list-view/use-list-params';
import { useWorkspaceFilter } from '@/lib/list-view/use-workspace-filter';
import { ListEmptyState, TestBadge, TestWorkspacesPill } from '@/components/test-workspaces';
import { ALL_WORKSPACES_QUERY, includeTestWorkspacesQuery } from '@/lib/test-workspaces/include-test-workspaces';
import {
  CalendarIcon,
  ClockIcon,
  InvestmentIcon,
  MembersIcon,
  PropertiesIcon,
  TagIcon,
  WorkspacesIcon,
} from '@/components/layout/icons';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';

/*
 * IMM-655.1 — Workspace and Objekt stay pinned while the remaining nine columns
 * scroll, so a row is still identifiable once the table is scrolled right. This
 * is IMM-617.1's shape copied from the customer app's Objekte list, not a second
 * solution — the two surfaces share no code, which is exactly why the same defect
 * had to be fixed twice (BL-051).
 *
 * `position`/`left`/`width` are INLINE rather than Tailwind utilities on purpose:
 * Objekt's offset is Workspace's width, and deriving it here keeps the two from
 * drifting apart (a dynamic `left-[…px]` class would not compile at all, and a
 * restated literal is exactly the drift this avoids). It also makes the pin an
 * assertable computed style rather than a class string (BL-034).
 */
const PINNED_WORKSPACE_WIDTH_PX = 200;
const PINNED_OBJECT_WIDTH_PX = 260;

/**
 * IMM-668, inherited rather than rediscovered — `maxWidth` is the one that
 * actually bounds these columns.
 *
 * The table is `table-layout: auto`, where a declared `width` is an INPUT to the
 * sizing algorithm rather than a constraint: the algorithm exceeds it to fit
 * content. `minWidth` bounds the column from below, which is the opposite of
 * what is needed. Only `maxWidth` is honoured as a ceiling, so each column is
 * bracketed on both sides and the pinned block costs a fixed 200 + 260 whatever
 * the content (BL-065). Objekt is the column this matters most for: it was
 * `min-w-[220px]` — bounded from below only — and it carries the longest
 * content in the table.
 */
const PINNED_WORKSPACE_STYLE = {
  position: 'sticky',
  left: 0,
  width: PINNED_WORKSPACE_WIDTH_PX,
  minWidth: PINNED_WORKSPACE_WIDTH_PX,
  maxWidth: PINNED_WORKSPACE_WIDTH_PX,
} as const satisfies CSSProperties;

const PINNED_OBJECT_STYLE = {
  position: 'sticky',
  left: PINNED_WORKSPACE_WIDTH_PX,
  width: PINNED_OBJECT_WIDTH_PX,
  minWidth: PINNED_OBJECT_WIDTH_PX,
  maxWidth: PINNED_OBJECT_WIDTH_PX,
} as const satisfies CSSProperties;

// Opaque at every row state, or the scrolling columns show through beneath the
// pinned ones. This queue's rows are `interactive` and NEVER `selected`, so the
// states that exist here are default and hover — and `group` on the row is what
// lets a cell follow its row's hover background, since `TableRow` in the ui-kit
// emits `cursor-pointer hover:bg-hover-bg` but no `group` of its own.
const PINNED_BODY_CELL_CLASS = 'z-10 bg-surface group-hover:bg-hover-bg group-focus-visible:bg-hover-bg';
const PINNED_HEAD_CELL_CLASS = 'z-20 bg-neutral-50';
// On the LAST pinned column only, so the seam reads as intentional rather than as
// a rendering artefact. An inset shadow, not a border: `border-collapse` drops
// borders on sticky cells once they detach from the grid.
//
// IMM-839 — and ONLY while the container actually overflows. This marks the edge
// the scrolling columns pass behind, so with nothing scrolling there is no edge
// and the seam brackets the pinned pair off for no reason a reader can attribute
// to anything on screen. The condition is measured, never assumed: see the
// `useLayoutEffect` in the component.
const PINNED_SEAM_CLASS = 'shadow-[inset_-1px_0_0_0_var(--color-neutral-200)]';

// The pinned pair is a locked leading block: the `left` offsets above only hold while it never reorders.
const LOCKED_LEADING_COLUMNS: ReadonlyArray<string> = ['workspace', 'object'];

type ValuationQueueLoaderResult = {
  readonly requests: ReadonlyArray<AdminValuationQueueItem>;
  readonly organizations: ReadonlyArray<AdminOrganizationListItem>;
  readonly columnPreference: ColumnPreference;
};

// The status filter is server-side, read from the URL search params so the loader
// and any shareable/deep link agree (the `api-keys.tsx` pattern). Only a known
// filter value is ever forwarded — a single admin-visible status or the `ALL`
// sentinel (every admin-visible status). Anything else clears the filter and the
// backend returns the active default (REQUESTED + IN_PROGRESS).
// The test-workspace flag is NOT read from the URL: it is the shared,
// browser-persisted filter (IMM-593.3), spread in at the call site from
// `includeTestWorkspacesQuery()`.
type ListAdminValuationRequestsFilters = Omit<ListAdminValuationRequestsQuery, 'includeTestWorkspaces'>;

function queryFromParams(searchParams: URLSearchParams): ListAdminValuationRequestsFilters {
  const query: ListAdminValuationRequestsFilters = {};
  const status = searchParams.get('status');

  if (isStatusFilter(status)) query.status = status;

  return query;
}

function isStatusFilter(value: string | null): value is ValuationAdminStatusFilter {
  return value !== null && (VALUATION_ADMIN_STATUS_FILTERS as ReadonlyArray<string>).includes(value);
}

export const valuationRequestsLoaderParams = ['status'] as const;

// The list fans out the cross-tenant request read (filtered by the URL `status`)
// + the organisation list (to resolve workspace names client-side, the
// `properties.tsx` pattern). The backend orders oldest `requestedAt` first — the
// page keeps that order.
export async function valuationRequestsLoader({ request }: LoaderFunctionArgs): Promise<ValuationQueueLoaderResult> {
  const query = queryFromParams(new URL(request.url).searchParams);

  const [requests, organizations, columnPreference] = await Promise.all([
    apiClient.adminValuationRequests.list({ query: { ...query, ...includeTestWorkspacesQuery() } }),
    // A name resolver for the rows and the workspace pills, not a list of its
    // own: asked for every workspace so a test-workspace request shown under an
    // active filter still resolves its workspace name (IMM-593.3).
    apiClient.adminOrganizations.list({ query: ALL_WORKSPACES_QUERY }),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.valuationRequests),
  ]);

  if (!requests.ok) {
    throw new Response('Failed to load valuation requests', { status: requests.status });
  }

  if (!organizations.ok) {
    throw new Response('Failed to load organisations', { status: organizations.status });
  }

  return { requests: requests.body.data, organizations: organizations.body.data, columnPreference };
}

type StatusTone = 'requested' | 'in-progress' | 'awaiting-release' | 'released';

// Total over `ValuationAdminStatus`: a status added to the union without a tone is
// a compile error, never a badge that renders with no colour (BL-024).
const STATUS_TONE: Record<ValuationAdminStatus, StatusTone> = {
  REQUESTED: 'requested',
  IN_PROGRESS: 'in-progress',
  AWAITING_RELEASE: 'awaiting-release',
  RELEASED: 'released',
};

const STATUS_CLASSES: Record<StatusTone, { wrap: string; dot: string }> = {
  requested: { wrap: 'bg-neutral-100 text-text-secondary', dot: 'bg-neutral-400' },
  'in-progress': { wrap: 'bg-success-50 text-success-700', dot: 'bg-success-500' },
  'awaiting-release': { wrap: 'bg-warning-50 text-warning-700', dot: 'bg-warning-500' },
  released: { wrap: 'bg-info-50 text-info-700', dot: 'bg-info-500' },
};

const QUEUE_STATUSES: ReadonlyArray<ValuationAdminStatus> = VALUATION_ADMIN_STATUSES;

// Age from the submission stamp — days, else hours, else minutes, else "just
// now". Oldest-first + age is the no-SLA mitigation (Resolved Decision 21).
function ageLabel(iso: string, t: (key: string, values?: Record<string, number>) => string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);

  if (minutes < 1) return t('age.now');

  const hours = Math.floor(minutes / 60);

  if (hours < 1) return t('age.minutes', { count: minutes });

  const days = Math.floor(hours / 24);

  if (days < 1) return t('age.hours', { count: hours });

  return t('age.days', { count: days });
}

// `desiredCompletion` is a date-only string. `new Date('2026-08-01')` is UTC midnight, so
// any timezone west of UTC would render the previous day — the explicit midnight suffix
// makes it parse as LOCAL midnight instead. Matches „Angefragt am" with `dateStyle: 'short'`.
function desiredCompletionLabel(
  value: string | null,
  intl: ReturnType<typeof useIntl>,
  t: (key: string) => string,
): string {
  return value === null ? t('unassigned') : intl.formatDate(`${value}T00:00:00`, { dateStyle: 'short' });
}

type ValuationColumnsOptions = {
  readonly workspaceNames: ReadonlyMap<string, string>;
  readonly seam: boolean;
};

function useValuationRequestColumns({
  workspaceNames,
  seam,
}: ValuationColumnsOptions): ReadonlyArray<OperatorColumn<AdminValuationQueueItem>> {
  const t = useTranslations('pages.admin.valuations');
  const tObjectType = useTranslations('pages.admin.valuations.detail.objectType');
  const tUsage = useTranslations('pages.admin.valuations.detail.usage');
  const intl = useIntl();

  return useMemo(() => {
    // Applied to the LAST pinned column's header and body cells, or to neither.
    const pinnedSeamClass = seam ? ` ${PINNED_SEAM_CLASS}` : '';

    return [
      {
        id: 'workspace',
        label: t('columns.workspace'),
        icon: WorkspacesIcon,
        headClassName: PINNED_HEAD_CELL_CLASS,
        headStyle: PINNED_WORKSPACE_STYLE,
        cellClassName: PINNED_BODY_CELL_CLASS,
        cellStyle: PINNED_WORKSPACE_STYLE,
        // `min-w-0` on both the row and the span: a flex item's
        // automatic minimum size is its content, so without it the
        // `truncate` stays inert and the text pushes out of the (now
        // bounded) cell instead (BL-042). The `title` keeps the full
        // string available on hover, so truncating loses nothing.
        cell: (item) => (
          <div className="flex min-w-0 items-center gap-2 text-text-primary">
            <span className="min-w-0 truncate" title={workspaceNames.get(item.workspaceId)}>
              {workspaceNames.get(item.workspaceId) ?? t('unassigned')}
            </span>
            <TestBadge isTest={item.isTest} />
          </div>
        ),
        sortValue: (item) => workspaceNames.get(item.workspaceId) ?? null,
      },
      {
        id: 'object',
        label: t('columns.object'),
        icon: PropertiesIcon,
        headClassName: `${PINNED_HEAD_CELL_CLASS}${pinnedSeamClass}`,
        headStyle: PINNED_OBJECT_STYLE,
        cellClassName: `${PINNED_BODY_CELL_CLASS}${pinnedSeamClass}`,
        cellStyle: PINNED_OBJECT_STYLE,
        cell: (item) => (
          <div className="truncate text-text-primary" title={item.address ?? undefined}>
            {item.address ?? t('unassigned')}
          </div>
        ),
        sortValue: (item) => item.address,
      },
      {
        id: 'objectType',
        label: t('columns.objectType'),
        icon: TagIcon,
        headClassName: 'w-[180px]',
        cell: (item) => (item.objectType === null ? t('unassigned') : tObjectType(item.objectType)),
        sortValue: (item) => (item.objectType === null ? null : tObjectType(item.objectType)),
      },
      {
        id: 'usage',
        label: t('columns.usage'),
        icon: TagIcon,
        headClassName: 'w-[130px]',
        cell: (item) => (item.usage === null ? t('unassigned') : tUsage(item.usage)),
        sortValue: (item) => (item.usage === null ? null : tUsage(item.usage)),
      },
      {
        id: 'requestedBy',
        label: t('columns.requested-by'),
        icon: MembersIcon,
        headClassName: 'w-[170px]',
        cellClassName: 'text-text-secondary',
        cell: (item) => item.requestedByName ?? t('unassigned'),
        sortValue: (item) => item.requestedByName,
      },
      {
        id: 'requestedAt',
        label: t('columns.requested-at'),
        icon: CalendarIcon,
        headClassName: 'w-[140px]',
        cellClassName: 'text-text-tertiary tabular-nums',
        cell: (item) => intl.formatDate(item.requestedAt, { dateStyle: 'short' }),
        sortValue: (item) => Date.parse(item.requestedAt),
      },
      {
        id: 'desiredCompletion',
        label: t('columns.desired-completion'),
        icon: CalendarIcon,
        headClassName: 'w-[160px]',
        cellClassName: 'text-text-tertiary tabular-nums',
        cell: (item) => desiredCompletionLabel(item.desiredCompletion, intl, t),
        sortValue: (item) => (item.desiredCompletion === null ? null : Date.parse(item.desiredCompletion)),
      },
      {
        id: 'age',
        label: t('columns.age'),
        icon: ClockIcon,
        headClassName: 'w-[110px]',
        cellClassName: 'text-text-tertiary tabular-nums',
        cell: (item) => ageLabel(item.requestedAt, t),
        // The negated request time is the order the age shows, without reading the clock inside a sort key.
        sortValue: (item) => -Date.parse(item.requestedAt),
      },
      {
        id: 'recommendedStartPrice',
        label: t('columns.recommended-start-price'),
        icon: InvestmentIcon,
        headClassName: 'w-[160px]',
        cellClassName: 'text-text-tertiary tabular-nums',
        cell: (item) =>
          item.recommendedStartPrice === null ? t('unassigned') : intl.formatNumber(item.recommendedStartPrice),
        sortValue: (item) => item.recommendedStartPrice,
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TagIcon,
        headClassName: 'w-[150px]',
        cell: (item) => {
          const statusClasses = STATUS_CLASSES[STATUS_TONE[item.status]];

          return (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-base-sm ${statusClasses.wrap}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${statusClasses.dot}`} aria-hidden />
              {t(`status.${item.status}`)}
            </span>
          );
        },
        sortValue: (item) => t(`status.${item.status}`),
      },
      {
        id: 'assignee',
        label: t('columns.assignee'),
        icon: MembersIcon,
        headClassName: 'w-[170px]',
        cellClassName: 'text-text-secondary',
        cell: (item) => claimHolderLabel(item.takenByKind, item.takenByName, t) ?? t('unassigned'),
        sortValue: (item) => claimHolderLabel(item.takenByKind, item.takenByName, t),
      },
    ];
  }, [t, tObjectType, tUsage, intl, workspaceNames, seam]);
}

export function ValuationRequestsPage() {
  const t = useTranslations('pages.admin.valuations');
  const tFilters = useTranslations('pages.admin.shell.filters');
  const { requests, organizations, columnPreference } = useLoaderData() as ValuationQueueLoaderResult;
  const navigate = useNavigate();

  // workspaceId → name (an org carries both; many orgs may share a workspace).
  const workspaceNames = useMemo(() => {
    const map = new Map<string, string>();

    for (const org of organizations) {
      if (!map.has(org.workspaceId)) map.set(org.workspaceId, org.workspaceName);
    }

    return map;
  }, [organizations]);

  const { params, setParams } = useListParams();
  const statusParam = params.get('status');
  const selectedFilter = isStatusFilter(statusParam) ? statusParam : null;
  const search = params.get(SEARCH_PARAM) ?? '';

  // The workspace filter lives in the URL too, though it narrows client-side: as
  // component state it was lost on browser Back from a request, dropping the
  // operator's place in a busy queue (IMM-969 QA-11, ux-rubric UX-5).
  const workspaceRows = useMemo(
    () =>
      requests.map((request) => ({
        workspaceId: request.workspaceId,
        workspaceName: workspaceNames.get(request.workspaceId) ?? null,
      })),
    [requests, workspaceNames],
  );
  const workspace = useWorkspaceFilter(workspaceRows, {
    labels: { label: t('filters.more-workspaces'), placeholder: t('filters.more-workspaces-placeholder') },
  });
  const workspaceFilter = workspace.workspaceId;

  // The status filter is committed to the URL so the loader refetches server-side;
  // selecting the active pill again clears it back to the active default. `ALL` is
  // a first-class value here — the "show every status" pill.
  function toggleStatus(status: ValuationAdminStatusFilter): void {
    setParams({ status: selectedFilter === status ? null : status });
  }

  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return requests.filter((item) => {
      // Status is filtered server-side (via the URL `status` param → the loader);
      // workspace + free-text search stay client-side over the returned set.
      if (workspaceFilter !== null && item.workspaceId !== workspaceFilter) return false;

      if (needle !== '') {
        const haystack = [
          item.address ?? '',
          workspaceNames.get(item.workspaceId) ?? '',
          // The COMPOSED assignee label, not the raw name (IMM-572.2): an operator
          // who can see „Admin-API-Schlüssel" on screen will type it into the search.
          claimHolderLabel(item.takenByKind, item.takenByName, t) ?? '',
          item.requestedByName ?? '',
        ]
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(needle)) return false;
      }

      return true;
    });
  }, [requests, workspaceFilter, search, workspaceNames, t]);

  /* IMM-839 — the seam below is evidence of a horizontal scroll, so it may only
     paint while there IS one. That cannot be expressed in CSS: whether the eleven
     columns overflow depends on the admin sidebar's width and on the current
     column set, so a media-query breakpoint would be a guess that goes stale the
     moment the columns change. It has to be measured from real layout.

     BOTH boxes are observed, because overflow is a comparison of two independently
     moving numbers. The container's width follows the window and the sidebar; the
     content's width follows the data, since `table-layout: auto` lets the nine
     unpinned columns exceed their declared widths to fit their text (the IMM-668
     note above). Observing the container alone would miss every change of the
     second kind — a status filter that swaps five short „Bearbeiter" labels for
     five long ones changes the table's width, the row COUNT not at all, and the
     container's box not at all, so the seam would sit stale in whichever state it
     was last measured in. That is this very bug, returning through the one door a
     container-only observer leaves open.

     The row count stays in the dependency list for a reason the observers cannot
     cover: the scroll container does not exist while the queue is empty, so this
     is what re-attaches them when the empty state gives way to the table. */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;

    if (el === null || content === null || typeof ResizeObserver === 'undefined') return;

    // A layout-less environment (jsdom) reports every box as 0 wide, so this reads
    // `0 > 0` — false, and the seam stays off. Off is the safe default: asserting a
    // scroll that is not happening is the defect this fixes.
    const measure = () => setOverflowing(el.scrollWidth > el.clientWidth);

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(el);
    observer.observe(content);

    return () => observer.disconnect();
  }, [filtered.length]);

  const catalogue = useValuationRequestColumns({ workspaceNames, seam: overflowing });
  const table = useOperatorTable({
    rows: filtered,
    preferenceKey: OPERATOR_COLUMN_KEYS.valuationRequests,
    catalogue,
    alwaysVisibleId: 'object',
    lockedLeadingIds: LOCKED_LEADING_COLUMNS,
    loadedPreference: columnPreference,
  });

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={filtered.length} icon={InvestmentIcon} />} />

      {/* IMM-655.1 — `h-full min-h-0` (not `min-h-full`): the page fills the
          shell's `<main>` exactly instead of growing past it, so the table region
          below can be a BOUNDED scroll container. A scroll container paints its
          scrollbar at its own edge, and this one used to have no bottom edge above
          the fold (BL-051). `PageTopbar` portals into the shell topbar and
          contributes nothing to this flow, so it stays outside the column.

          The trade-off, restated so it is not rediscovered as a bug: `<main>`'s own
          `overflow-y-auto` becomes inert for this page — the table scrolls within
          its own region while the topbar and the filter bar stay put. */}
      <div className="flex h-full min-h-0 flex-col">
        <FilterBar
          /* OUTSIDE the two-row cap, not merely before it. The cap clips
             whatever does not fit, and the „Weitere Kunden" outlet carries
             workspaces only — so a status pill or the „Testumgebungen" toggle
             the cap swallows is a filter with no way back (BL-073, BL-122).
             Rendering them first survives 1440, where six pills fit in two
             rows; at 1024 the group is a third as wide, the two rows end after
             „In Bearbeitung", and „Warte auf Freigabe", „Freigegeben" and
             „Testumgebungen" disappear outright (IMM-969 QA-17, IMM-823 QA-15).
             `fixedPills` is unbounded and wraps as far as it must. */
          fixedPills={
            <>
              <FilterPill
                icon={TagIcon}
                label={t('status.ALL')}
                active={selectedFilter === 'ALL'}
                onToggle={() => toggleStatus('ALL')}
              />
              {QUEUE_STATUSES.map((status) => (
                <FilterPill
                  key={status}
                  icon={TagIcon}
                  label={t(`status.${status}`)}
                  active={selectedFilter === status}
                  onToggle={() => toggleStatus(status)}
                />
              ))}
              <TestWorkspacesPill />
            </>
          }
          pills={workspace.pills}
          searchValue={search}
          searchPlaceholder={tFilters('search')}
          onSearchChange={(next, commit) => setParams({ [SEARCH_PARAM]: next }, { replace: commit.continuing })}
          trailing={<ColumnsTrigger {...table.columns.trigger} />}
          overflow={workspace.overflow}
        />
        {filtered.length === 0 ? (
          <ListEmptyState title={t('empty')} serverEmpty={requests.length === 0} />
        ) : (
          /* 11 columns overflow the content area beside the admin sidebar, so the
             table scrolls rather than letting `table-layout: auto` squeeze „Objekt"
             — the longest content in the row. This is the scroller on BOTH axes and
             the only element below the filter bar, so its bottom edge is the bottom
             of the VISIBLE area, which is where the browser paints the horizontal
             bar (IMM-655.1). It used to be `overflow-x-auto` on a container of
             content-driven height, which put that bar below the fold.

             The `w-max min-w-full` wrapper is what the old comment's `min-w` was
             reaching for, and it is still load-bearing: without it the table's own
             `w-full` resolves to the scroller's width and the columns squash toward
             min-content instead of overflowing — destroying the very scroll this
             exists to expose. */
          /* `tabIndex={0}` because this is the only way the last columns
             („Status", „Bearbeiter") come into view without a pointer: the
             scroller is not reachable by Tab otherwise, and no control inside
             it is focusable either, so a keyboard-only operator could not
             scroll it at all (IMM-824 QA-21). `0`, not `-1`, for the same
             reason the customer Objekte list uses it: some browsers give a
             scroll container an implicit tab stop and some do not, and this
             takes one in both. The ring is an INSET OUTLINE — an outward halo
             or a box-shadow paints beneath the opaque pinned cells and behind
             the surrounding layout, and `focus-visible:` keeps it off for the
             mouse. */
          <div
            ref={scrollRef}
            tabIndex={0}
            className="min-h-0 flex-1 overflow-auto focus-visible:outline focus-visible:outline-4 focus-visible:-outline-offset-4 focus-visible:outline-focus-inset"
            data-testid="valuation-requests-table-scroll"
          >
            <div ref={contentRef} className="w-max min-w-full">
              <Table flush>
                <TableHeader>
                  <TableRow {...table.columns.headerRowProps}>
                    {table.columns.visibleColumns.map((column) => (
                      <OperatorColumnHead key={column.id} column={column} table={table} />
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {table.sorted.map((item) => (
                    // A tab stop that opens on Enter as well as click: the row
                    // is the queue's only way into a request, and a mouse-only
                    // row left it unreachable by keyboard (IMM-969 QA-13,
                    // DESIGN.md §5). The row focus treatment, not the halo.
                    <TableRow
                      key={item.id}
                      interactive
                      tabIndex={0}
                      className="group focus-visible:outline-none focus-visible:bg-hover-bg focus-visible:shadow-focus-row"
                      onClick={() => navigate(`/valuations/${item.id}`)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && event.target === event.currentTarget) {
                          navigate(`/valuations/${item.id}`);
                        }
                      }}
                    >
                      {table.columns.visibleColumns.map((column) => (
                        <TableCell
                          key={column.id}
                          className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                          style={column.cellStyle}
                        >
                          {column.cell(item)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
